// src/components/ModelMonitoring.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';

export default function ModelMonitoring() {
  const toast = useToast();
  const [modelStatus, setModelStatus] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trainingInProgress, setTrainingInProgress] = useState(false);

  const token = localStorage.getItem('token') || JSON.parse(localStorage.getItem('user') || 'null')?.token;
  const userRole = JSON.parse(localStorage.getItem('user') || 'null')?.role;

  useEffect(() => {
    fetchModelStatus();
    const interval = setInterval(fetchModelStatus, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchModelStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/model-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModelStatus(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch model status:', err);
      setError('Failed to load model status');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModels(res.data.models || []);
    } catch (err) {
      console.error('Failed to fetch models:', err);
    }
  };

  const handleTrainModel = async () => {
    if (!modelStatus?.readyToRetrain) {
      toast.error('Not enough labeled data. Need at least 10 labeled landings.');
      return;
    }

    if (!window.confirm('Start model training? This may take a few minutes.')) return;

    setTrainingInProgress(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/train-model`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Model training started! Check back in a few moments.');
      setTrainingInProgress(false);
      setTimeout(fetchModelStatus, 3000);
    } catch (err) {
      console.error('Training failed:', err);
      toast.error(`Training failed: ${err.response?.data?.error || err.message}`);
      setTrainingInProgress(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading model status...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>🤖 Model Training & Monitoring</h1>

      {error && (
        <div style={{ background: '#fee', color: '#c33', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Main Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {/* Labeled Data Card */}
        <div style={{ background: '#e7f3ff', padding: '20px', borderRadius: '8px', border: '2px solid #0066cc' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>📊 Labeled Landings</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#0066cc', marginBottom: '5px' }}>
            {modelStatus?.labeledLandings || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {modelStatus?.readyToRetrain ? '✅ Ready to train' : '⚠️ Need 10 minimum'}
          </div>
        </div>

        {/* Unlabeled Data Card */}
        <div style={{ background: '#fff3e0', padding: '20px', borderRadius: '8px', border: '2px solid #ff9800' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>📋 Unlabeled Landings</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff9800', marginBottom: '5px' }}>
            {modelStatus?.unlabeledLandings || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Waiting for classification
          </div>
        </div>

        {/* Progress Card */}
        <div style={{ background: '#f0f7ff', padding: '20px', borderRadius: '8px', border: '2px solid #4a90e2' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>📈 Labeling Progress</div>
          <div style={{ width: '100%', height: '30px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden', marginBottom: '5px' }}>
            <div
              style={{
                width: `${modelStatus?.totalProcessedVideos > 0 ? (modelStatus?.labeledLandings / modelStatus?.totalProcessedVideos * 100) : 0}%`,
                height: '100%',
                background: '#4a90e2',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {modelStatus?.labeledLandings} / {modelStatus?.totalProcessedVideos} labeled
          </div>
        </div>
      </div>

      {/* Active Model Info */}
      {modelStatus?.activeModel ? (
        <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #ddd' }}>
          <h2 style={{ marginTop: 0 }}>🚀 Active Model</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Model Name</div>
              <div style={{ fontSize: '16px' }}>{modelStatus.activeModel.modelName}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Version</div>
              <div style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                {modelStatus.activeModel.version.substring(0, 19)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Accuracy</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>
                {(modelStatus.activeModel.accuracy * 100).toFixed(1)}%
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Training Samples</div>
              <div style={{ fontSize: '16px' }}>{modelStatus.activeModel.nSamples}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Classes</div>
              <div style={{ fontSize: '14px' }}>
                {modelStatus.activeModel.classes.join(', ')}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>Trained</div>
              <div style={{ fontSize: '14px' }}>
                {new Date(modelStatus.activeModel.trainedAt).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff3cd', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #ffc107' }}>
          <p>⚠️ No active model yet. Train one to get started!</p>
        </div>
      )}

      {/* Train Model Button */}
      {userRole === 'admin' && (
        <div style={{ marginBottom: '30px' }}>
          <button
            onClick={handleTrainModel}
            disabled={!modelStatus?.readyToRetrain || trainingInProgress}
            style={{
              padding: '15px 30px',
              fontSize: '16px',
              fontWeight: 'bold',
              background: modelStatus?.readyToRetrain ? '#28a745' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: modelStatus?.readyToRetrain && !trainingInProgress ? 'pointer' : 'not-allowed',
              opacity: modelStatus?.readyToRetrain && !trainingInProgress ? 1 : 0.6,
            }}
          >
            {trainingInProgress ? '⏳ Training...' : '🔄 Train Model'}
          </button>
          {!modelStatus?.readyToRetrain && (
            <div style={{ fontSize: '12px', color: '#c33', marginTop: '5px' }}>
              Need {10 - (modelStatus?.labeledLandings || 0)} more labeled landings to train
            </div>
          )}
        </div>
      )}

      {/* Model History */}
      <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd' }}>
        <h2 style={{ marginTop: 0 }}>📜 Model History</h2>
        <button
          onClick={fetchModels}
          style={{
            padding: '8px 16px',
            fontSize: '12px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '15px'
          }}
        >
          {models.length > 0 ? 'Refresh' : 'Load'} History
        </button>

        {models.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#e0e0e0', borderBottom: '2px solid #999' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Version</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Accuracy</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Samples</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Classes</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Trained</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '12px' }}>
                      {model.version.substring(0, 19)}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold', color: '#28a745' }}>
                      {(model.accuracy * 100).toFixed(1)}%
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{model.nSamples}</td>
                    <td style={{ padding: '10px', textAlign: 'center', fontSize: '12px' }}>
                      {model.nClasses}
                    </td>
                    <td style={{ padding: '10px', fontSize: '12px' }}>
                      {new Date(model.trainedAt).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      {model.active ? (
                        <span style={{ background: '#d4edda', color: '#155724', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                          Active
                        </span>
                      ) : (
                        <span style={{ background: '#e2e3e5', color: '#383d41', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                          Inactive
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: '#666' }}>No model history available yet.</p>
        )}
      </div>
    </div>
  );
}
